import React from 'react';
import { useVoucherSummary } from '../useVoucherSummary';

/**
 * A highly compressed, layman-friendly financial KPI strip for dashboard headers.
 * @param {Object} props
 * @param {Array} props.voucherList - Array of voucher objects
 */
const DayBookSummary = ({ voucherList = [] }) => {
  const summary = useVoucherSummary({ voucherList });

  // Compact money formatter
  const formatMoney = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0 // Removes decimal noise to save space in headers
    }).format(value);
  };

  return (
    <div className="bg-white border-bottom shadow-sm py-3 px-4 mb-2">
      
      {/* ROW 1: Core Operations (Sales & Purchases) */}
      <div className="row g-2 align-items-center mb-3">
        
        {/* Sales Block */}
        <div className="col-12 col-md-4">
          <div className="p-2 rounded bg-success bg-opacity-10 border border-success border-opacity-25 d-flex justify-content-between align-items-center">
            <div>
              <small className="text-success fw-bold d-block text-uppercase style={{ fontSize: '0.75rem' }}">📈 Total Sales</small>
              <small className="text-muted d-block small">Before Tax: {formatMoney(summary.totalTaxableSale)}</small>
            </div>
            <span className="h4 fw-bold text-success mb-0">{formatMoney(summary.totalSale)}</span>
          </div>
        </div>

        {/* Purchases Block */}
        <div className="col-12 col-md-4">
          <div className="p-2 rounded bg-danger bg-opacity-10 border border-danger border-opacity-25 d-flex justify-content-between align-items-center">
            <div>
              <small className="text-danger fw-bold d-block text-uppercase style={{ fontSize: '0.75rem' }}">📉 Total Purchases</small>
              <small className="text-muted d-block small">Before Tax: {formatMoney(summary.totalTaxablePurchase)}</small>
            </div>
            <span className="h4 fw-bold text-danger mb-0">{formatMoney(summary.totalPurchase)}</span>
          </div>
        </div>

        {/* Tax Block */}
        <div className="col-12 col-md-4">
          <div className="p-2 rounded bg-dark bg-opacity-10 border border-dark border-opacity-25">
            <div className="d-flex justify-content-between small text-muted mb-1">
              <span>🏛️ Tax Paid (Input):</span>
              <span className="fw-semibold text-primary">{formatMoney(summary.totalTaxInput)}</span>
            </div>
            <div className="d-flex justify-content-between small text-muted">
              <span>🏛️ Tax Due (Output):</span>
              <span className="fw-semibold text-warning-emphasis">{formatMoney(summary.totalTaxOutput)}</span>
            </div>
          </div>
        </div>

      </div>

      {/* ROW 2: Money Flows & Internal Adjustments (Micro Badges) */}
      <div className="row g-2 small text-muted align-items-center">
        
        {/* Actual Bank Flow */}
        <div className="col-6 col-sm-3 border-end">
          <div className="px-2">
            <span className="d-block text-muted text-truncate" title="Real money received">💰 Money In (Receipts)</span>
            <strong className="text-success">{formatMoney(summary.totalPaymentIn)}</strong>
          </div>
        </div>

        <div className="col-6 col-sm-3 border-end-sm">
          <div className="px-2">
            <span className="d-block text-muted text-truncate" title="Real cash spent">💸 Money Out (Payments)</span>
            <strong className="text-danger">{formatMoney(summary.totalPaymentOut)}</strong>
          </div>
        </div>

        {/* Returns / Notes */}
        <div className="col-6 col-sm-3 border-end">
          <div className="px-2">
            <span className="d-block text-muted text-truncate" title="Sales Returns">🔄 Credit Notes: <strong className="text-dark">{formatMoney(summary.totalCreditNote)}</strong></span>
            <span className="d-block text-muted text-truncate" title="Purchase Returns">↩️ Debit Notes: <strong className="text-dark">{formatMoney(summary.totalDebitNote)}</strong></span>
          </div>
        </div>

        {/* Internal Ledger Shifts */}
        <div className="col-6 col-sm-3">
          <div className="px-2">
            <span className="d-block text-muted text-truncate" title="Bank-to-Cash or vice versa">🏦 Internal Transfers: <strong className="text-secondary">{formatMoney(summary.totalContra)}</strong></span>
            <span className="d-block text-muted text-truncate" title="Manual non-cash adjustments">📝 Audit Changes: <strong className="text-secondary">{formatMoney(summary.totalJournal)}</strong></span>
          </div>
        </div>

      </div>

    </div>
  );
};

export default DayBookSummary;